import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { color, font, formTheme } from '../styles/theme';
import { IconBell, IconClose } from './icons';
import { opensTo, type NotificationEvent } from '../api/notifications';

/**
 * Arrival toasts, bottom-right.
 *
 * Rendered into `document.body` for the same reason the modal is: sections
 * wrap their content in `.fade-in`, whose keyframes leave an identity
 * transform on the element, and a transformed ancestor becomes the containing
 * block for `position: fixed` — which would pin these inside the content area
 * instead of the viewport.
 *
 * They name the form, never the person. The feed carries no identity by
 * design; see `api/notifications.ts`.
 */

const AUTO_DISMISS_MS = 9_000;

function Toast({
  event,
  onOpen,
  onDismiss,
}: {
  event: NotificationEvent;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [onDismiss]);

  const tone = formTheme[event.form as keyof typeof formTheme];

  return (
    <div
      role="status"
      className="toast-in"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: 340,
        maxWidth: 'calc(100vw - 32px)',
        background: color.white,
        border: `1px solid ${color.line}`,
        borderLeft: `3px solid ${tone?.donut ?? color.blue}`,
        borderRadius: 14,
        padding: '13px 14px',
        boxShadow: '0 12px 32px rgba(18,38,63,0.16)',
      }}
    >
      <span
        style={{
          display: 'flex',
          flex: 'none',
          width: 30,
          height: 30,
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          background: tone?.bg ?? color.tint,
          color: tone?.fg ?? color.blueDeep,
        }}
      >
        <IconBell size={15} />
      </span>

      <button
        onClick={onOpen}
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <span
          style={{
            display: 'block',
            fontFamily: font.display,
            fontWeight: 600,
            fontSize: 14,
            color: color.navy,
          }}
        >
          {event.type === 'submission' ? `New ${event.formLabel.toLowerCase()}` : event.formLabel}
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: color.muted, marginTop: 2 }}>
          {/* The kind repeats the form on single-kind forms; say it once. */}
          {event.kindLabel !== event.formLabel && `${event.kindLabel} · `}
          just now — <span style={{ color: color.blue, fontWeight: 700 }}>{opensTo(event)}</span>
        </span>
      </button>

      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="hov-tint2"
        style={{
          flex: 'none',
          background: 'transparent',
          border: 'none',
          color: color.muted,
          cursor: 'pointer',
          padding: 4,
          borderRadius: 8,
          lineHeight: 0,
        }}
      >
        <IconClose size={14} />
      </button>
    </div>
  );
}

export function Toasts({
  events,
  onOpen,
  onDismiss,
}: {
  events: NotificationEvent[];
  onOpen: (event: NotificationEvent) => void;
  onDismiss: (id: string) => void;
}) {
  if (events.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // The stack must never swallow clicks on the page behind it; each
        // toast re-enables pointer events for itself.
        pointerEvents: 'none',
      }}
    >
      {events.map((e) => (
        <div key={e.id} style={{ pointerEvents: 'auto' }}>
          <Toast event={e} onOpen={() => onOpen(e)} onDismiss={() => onDismiss(e.id)} />
        </div>
      ))}
    </div>,
    document.body,
  );
}
